import {
  FUSION_COUNT,
  getItem,
  type InventoryState,
  MOBS,
  type Rarity,
  RARITY_COLORS,
  TUTORIAL_FUSED,
  TUTORIAL_FUSED_FROM,
  TUTORIAL_RESOURCE,
  TUTORIAL_BOUGHT,
  TUTORIAL_UPGRADE_MATERIAL,
  type TutorialStage,
  tutorialStage,
  type TutorSkipRejected,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { actionKeyLabel } from '../settings/keybindings.js';
import { closeButton } from './hud.js';
import { addLevelBadge, paintItemIcon } from './item-icon.js';
import { uiFormat, uiText } from './text.js';
import './smith.css';
import './tutor.css';
import { kitTitle } from './kit.js';

/**
 * The tutor's advice (ADR 0030, ADR 0066): the camp's quests say what to do,
 * his window says why, at each step, with the very items it speaks of
 * (rarities, the smith's levels). For a character whose account already
 * knows the camp, a button sends them straight to Havrebourg. Like every
 * panel it only sends the intent; the server gives or refuses.
 */

const TUTOR_NAME = MOBS.tutor.name;

const SPEECH: Record<TutorialStage, string[]> = {
  hunt: [
    'Bienvenue au camp de la Compagnie Givrecoeur, recrue. Moi aussi, j’ai commencé torse nu.',
    'Les rats du terrain d’entraînement, au sud-est, laissent des peaux. Ramasse-les toutes : le marchand te les rachète, et avec cet or tu t’équiperas.',
  ],
  sell: [
    'Le marchand, près de l’étal, rachète ce que tu rapportes de la chasse.',
    'Vends-lui tes peaux : au camp, c’est ton seul or.',
  ],
  buy: [
    'Achète-lui les trois pièces qui manquent, et enfile-les avec ses deux cadeaux. C’est modeste, mais chaque pièce que tu portes se voit sur toi.',
  ],
  fuse: [
    'Chaque pièce existe en quatre raretés : commune, rare, épique et légendaire. La couleur du contour, dans ton sac, te la dit.',
    'Le fondeur fond {count} pièces identiques en une seule de la rareté au-dessus. Il ne prend que ce qui est dans ton sac.',
  ],
  wear: ['Une épée rare, déjà ! Équipe-la, avec tout ce que tu ne portes pas encore.'],
  forge: [
    'Le forgeron améliore une pièce de +1 à +10, avec les matériaux de sa région et un peu d’or.',
    'Les premiers niveaux réussissent toujours, les derniers presque jamais, mais un échec ne coûte que les matériaux. Mon équipement est à +10 : Hald l’a forgé pour Givrecime, et c’est là que ton chemin mène.',
  ],
  leave: [
    'Tu es prêt. L’arche bleue, à l’est, t’emmène à Havrebourg : porte ma lettre au capitaine Aldric.',
    'J’ai vu la glace se fendre sous Givrecime, et une lueur rouge dessous. On ne me croit pas encore. On me croira. Je t’attends au quartier de la Compagnie. Tant que le givre tient.',
  ],
};

const REFUSALS: Record<TutorSkipRejected['reason'], string> = {
  invalid: 'Demande invalide.',
  'not-allowed':
    'Aucun de tes autres personnages n’a quitté le camp : suis les leçons, cette fois.',
  'too-far': 'Approche-toi, que je te confie ton équipement.',
  'bag-full': 'Ton sac est plein : fais-y de la place.',
};

/** Where every step's advice ends: the quests lead, he explains. */
const FOLLOW_THE_QUESTS =
  'Les « ! » dorés au-dessus des habitants du camp marquent une quête à prendre, les « ? » une quête à rendre. Ton journal ({key}) garde le fil.';

/** One item of an example row: a template, how many, at what smith's level, and a caption. */
interface Shown {
  id: string;
  count?: number;
  level?: number;
  caption?: string;
}
/** An example row: items, and between two of them an arrow or a sign. */
type Example = (Shown | '→' | '+' | '…')[];

const RARITY_WORDS: Record<Rarity, string> = {
  common: 'Commune',
  rare: 'Rare',
  epic: 'Épique',
  legendary: 'Légendaire',
};
const OUTFIT = MOBS.tutor.outfit;
const TUTOR_PIECES =
  OUTFIT === undefined
    ? []
    : [OUTFIT.helmet, OUTFIT.chest, OUTFIT.weapon, OUTFIT.offhand, OUTFIT.gloves, OUTFIT.boots];

/** What each step shows, beside his words: the very items it speaks of. */
const EXAMPLES: Record<TutorialStage, Example[]> = {
  hunt: [[{ id: TUTORIAL_RESOURCE, caption: 'Au marchand' }]],
  sell: [[{ id: TUTORIAL_RESOURCE }, '→', { id: 'gold', caption: 'Or' }]],
  buy: [TUTORIAL_BOUGHT.map((id) => ({ id }))],
  fuse: [
    ['', '-rare', '-epic', '-legendary'].map((suffix) => {
      const id = `${TUTORIAL_FUSED_FROM}${suffix}`;
      return { id, caption: RARITY_WORDS[getItem(id)?.rarity ?? 'common'] };
    }),
    [{ id: TUTORIAL_FUSED_FROM, count: FUSION_COUNT }, '→', { id: TUTORIAL_FUSED }],
  ],
  wear: [[{ id: TUTORIAL_FUSED, caption: 'Rare' }]],
  forge: [
    [
      { id: TUTORIAL_FUSED, caption: '+0' },
      '+',
      { id: TUTORIAL_UPGRADE_MATERIAL },
      '→',
      { id: TUTORIAL_FUSED, level: 1, caption: '+1' },
      '…',
      { id: OUTFIT?.weapon ?? TUTORIAL_FUSED, level: 10, caption: '+10' },
    ],
  ],
  leave: [TUTOR_PIECES.map((id) => ({ id, level: OUTFIT?.level }))],
};

export interface TutorPanelOptions {
  /** The bag as the server last sent it; undefined before the first. */
  inventory: () => InventoryState | undefined;
  onSkip: () => void;
}

export class TutorPanel {
  private readonly root: HTMLElement;
  private readonly speech: HTMLElement;
  private readonly examples: HTMLElement;
  private icons: CssAtlas | null = null;
  private readonly skip: HTMLElement;
  private readonly action: HTMLButtonElement;
  private readonly message: HTMLElement;
  private inReach = false;
  private pending = false;
  private skipAvailable = false;
  /** The first click on « go ahead » asks again: a misclick costs the lessons. */
  private confirming = false;
  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !this.root.hidden) this.close();
  };

  constructor(private readonly options: TutorPanelOptions) {
    this.root = document.createElement('section');
    this.root.className = 'smith tutor';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText(TUTOR_NAME));

    const title = document.createElement('h2');
    title.className = 'smith__title tutor__title';
    kitTitle(title, 'tutor');
    const name = document.createElement('span');
    name.textContent = uiText(TUTOR_NAME);
    name.style.color = `#${RARITY_COLORS.legendary.toString(16).padStart(6, '0')}`;
    title.append(
      name,
      closeButton('Fermer (Échap)', () => {
        this.close();
      }),
    );
    const rank = document.createElement('p');
    rank.className = 'smith__muted tutor__rank';
    rank.textContent =
      OUTFIT?.mountName === undefined
        ? uiText('Guerrier · set de givre légendaire, +10')
        : uiFormat('Guerrier · set de givre légendaire, +10 · sur {mount}, son pégase', {
            mount: OUTFIT.mountName,
          });

    this.speech = document.createElement('div');
    this.speech.className = 'tutor__speech';
    this.examples = document.createElement('div');
    this.examples.className = 'tutor__examples';
    this.skip = document.createElement('p');
    this.skip.className = 'smith__muted tutor__skip';
    this.skip.textContent = uiText(
      'Tu connais déjà le camp : si tu veux, je te confie l’équipement des leçons, épée rare à +1 comprise, et tu pars tout de suite pour Havrebourg.',
    );
    this.action = document.createElement('button');
    this.action.type = 'button';
    this.action.className = 'smith__go';
    this.action.addEventListener('click', () => {
      if (!this.inReach || this.pending) return;
      if (!this.confirming) {
        this.confirming = true;
        this.render();
        return;
      }
      this.pending = true;
      this.render();
      this.options.onSkip();
    });
    this.message = document.createElement('p');
    this.message.className = 'smith__message';
    this.message.setAttribute('aria-live', 'polite');

    // One box for what may run long, so a short screen scrolls it alone (tutor.css).
    const scroll = document.createElement('div');
    scroll.className = 'tutor__scroll';
    scroll.append(this.speech, this.examples);
    this.root.append(title, rank, scroll, this.skip, this.action, this.message);
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKey);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  open(): void {
    this.root.hidden = false;
    this.confirming = false;
    this.message.textContent = '';
    delete this.message.dataset.tone;
    this.render();
  }

  close(): void {
    this.root.hidden = true;
  }

  setIcons(icons: CssAtlas | null): void {
    this.icons = icons;
    if (this.isOpen) this.render();
  }

  /** The inventory changed: the step, and so the speech, may have too. */
  refresh(): void {
    if (this.isOpen) this.render();
  }

  /** Whether the server offers this character to skip the camp (ADR 0066). */
  setSkipAvailable(available: boolean): void {
    this.skipAvailable = available;
    if (this.isOpen) this.render();
  }

  setInReach(inReach: boolean): void {
    if (inReach === this.inReach) return;
    this.inReach = inReach;
    this.render();
  }

  showRefusal(reason: TutorSkipRejected['reason']): void {
    this.pending = false;
    this.confirming = false;
    this.message.dataset.tone = 'refused';
    this.message.textContent = uiText(REFUSALS[reason]);
    this.render();
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKey);
    this.root.remove();
  }

  private render(): void {
    const inventory = this.options.inventory();
    const stage = inventory === undefined ? 'hunt' : tutorialStage(inventory);
    const lines = stage === 'leave' ? SPEECH[stage] : [...SPEECH[stage], FOLLOW_THE_QUESTS];
    this.speech.replaceChildren(
      ...lines.map((line) => {
        const paragraph = document.createElement('p');
        // The smelter's line counts its pieces; the others have no value to fill.
        paragraph.textContent = uiFormat(line, {
          count: FUSION_COUNT,
          key: actionKeyLabel('quests'),
        });
        return paragraph;
      }),
    );
    this.examples.replaceChildren(...EXAMPLES[stage].map((row) => this.exampleRow(row)));
    this.skip.hidden = !this.skipAvailable;
    this.action.hidden = !this.skipAvailable;
    this.action.textContent = uiText(
      this.confirming ? 'Confirmer : partir pour Havrebourg' : 'Aller directement à Havrebourg',
    );
    this.action.disabled = !this.inReach || this.pending;
    this.action.title = this.inReach ? '' : uiText('Approche-toi de lui.');
  }

  private exampleRow(row: Example): HTMLElement {
    const line = document.createElement('div');
    line.className = 'tutor__example';
    for (const entry of row) {
      if (typeof entry === 'string') {
        const sign = document.createElement('span');
        sign.className = 'tutor__sign';
        sign.textContent = entry;
        line.append(sign);
        continue;
      }
      const figure = document.createElement('figure');
      figure.className = 'tutor__figure';
      const slot = document.createElement('span');
      slot.className = 'tutor__item';
      const icon = document.createElement('span');
      icon.className = 'tutor__icon';
      if (entry.id === 'gold') {
        icon.classList.add('tutor__icon--gold');
      } else {
        const template = getItem(entry.id);
        paintItemIcon(icon, template, this.icons, entry.id);
        slot.title = uiText(template?.name ?? entry.id);
        // The rarity as the contour, as in the bag.
        if (template !== undefined && template.rarity !== 'common')
          slot.style.outline = `2px solid #${RARITY_COLORS[template.rarity].toString(16).padStart(6, '0')}`;
      }
      slot.append(icon);
      if (entry.level !== undefined) addLevelBadge(slot, entry.level);
      if (entry.count !== undefined) {
        const count = document.createElement('span');
        count.className = 'tutor__count';
        count.textContent = `×${String(entry.count)}`;
        slot.append(count);
      }
      figure.append(slot);
      if (entry.caption !== undefined) {
        const caption = document.createElement('figcaption');
        caption.textContent = uiText(entry.caption);
        figure.append(caption);
      }
      line.append(figure);
    }
    return line;
  }
}
